import { makeStyles } from '@material-ui/core';

export const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(3),
    paddingTop: 0,
    // The app never renders <CssBaseline /> and the global stylesheet sets no
    // body font, so bare <span>/<div> children fall back to the browser serif.
    // MUI only injects Roboto into Typography/TableCell. Declaring it here lets
    // every plain element in this view inherit the app font.
    fontFamily: theme.typography.fontFamily,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(3),
    paddingBottom: theme.spacing(2),
    borderBottom: '1px solid #E0E0E0',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(3),
  },
  backButton: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    color: '#000',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  title: {
    fontSize: '24px',
    fontWeight: 600,
  },
  batchGenealogyButton: {
    backgroundImage: 'linear-gradient(to right, #301232, #1A2D6C, #173072)',
    color: 'white',
    border: '1px solid black',
    textTransform: 'none',
    borderRadius: '2rem',
    minWidth: '180px',
    height: '40px',
    padding: '8px 20px',
    marginTop: '4px',
    '&:hover': {
      backgroundColor: 'green',
      background: 'green',
      color: 'white',
      border: '1px solid green',
    },
    // Disabled (feature not shipped yet): keep the gradient and white label
    // readable — MUI would otherwise fade the text to rgba(0,0,0,0.26), which
    // is invisible on this dark gradient. Opacity carries the disabled cue.
    '&.Mui-disabled': {
      backgroundImage: 'linear-gradient(to right, #301232, #1A2D6C, #173072)',
      color: 'white',
      border: '1px solid black',
      opacity: 0.5,
    },
  },
  filterSection: {
    marginTop: theme.spacing(3),
    marginBottom: 0,
  },
  // View Analytics — solid blue, lifts with a shadow on hover.
  viewAnalyticsButton: {
    backgroundColor: '#2F55D4',
    color: '#fff',
    textTransform: 'none',
    border: 'none',
    borderRadius: '8px',
    minWidth: '150px',
    height: '40px',
    padding: '8px 20px',
    marginTop: '4px',
    fontWeight: 600,
    fontSize: '14px',
    boxShadow: 'none',
    transition: 'box-shadow 0.2s ease, background-color 0.2s ease, transform 0.2s ease',
    '&:hover': {
      backgroundColor: '#2647B8',
      boxShadow: '0 6px 16px rgba(47, 85, 212, 0.4)',
      transform: 'translateY(-1px)',
    },
    '&.Mui-disabled': {
      backgroundColor: '#E0E0E0',
      color: '#9E9E9E',
      boxShadow: 'none',
    },
  },
  filterRow: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    borderBottom: '1px solid #E0E0E0',
    flexWrap: 'wrap',
  },
  filterControl: {
    minWidth: '200px',
  },
  filterControlSmall: {
    minWidth: '150px',
  },
  filterControlLarge: {
    minWidth: '300px',
  },
  // ── Results view (after "View Analytics") ──────────────────────────────────
  // Same vocabulary as the rest of Project Development: #000/#333 text,
  // #E0E0E0 borders, #F9F9FC fills, 4px radii, rem font sizes.
  resultsHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  // Results title in the brand navy (same #173072 as the primary buttons and the
  // panel headings below), not black.
  resultsTitle: {
    fontSize: '20px',
    fontWeight: 600,
    color: '#173072',
  },
  chipRow: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    marginTop: theme.spacing(1),
  },
  // Neutral context chips under the results title (project · campaign · counts):
  // black text on the standard #F9F9FC / #E0E0E0 grey.
  chip: {
    // 13px / 600 — a step up from the 12px regular they were, so the row reads
    // as a summary line rather than fine print. The type chip inherits the size
    // so all four stay visually level; only its colour differs.
    fontSize: '0.8125rem',
    fontWeight: 600,
    color: '#000',
    backgroundColor: '#F9F9FC',
    border: '1px solid #E0E0E0',
    borderRadius: '4px',
    padding: '3px 10px',
  },
  // Trailing test-type chip — deliberately the one tinted chip in the row so the
  // active test type reads as the subject of the page, not another neutral fact.
  // Tint is keyed to the brand navy (#173072, as in the primary buttons).
  // Declared after `chip` so JSS injects it later and wins at equal specificity.
  chipType: {
    color: '#173072',
    backgroundColor: '#E8EDF7',
    borderColor: '#C7D3EA',
    fontWeight: 600,
  },
  resultsSub: {
    fontSize: '0.875rem',
    color: '#000',
    marginTop: theme.spacing(0.5),
  },
  resultsFilterBar: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    backgroundColor: '#fff',
    border: '1px solid #E0E0E0',
    borderRadius: '4px',
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  searchAndSyncRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginTop: 0,
    marginBottom: theme.spacing(2),
  },
  filterLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    flex: 1,
    minWidth: 0,
  },
  // Filter icon + "Filters" caption on one baseline.
  filtersLabelWrap: {
    display: 'flex',
    alignItems: 'center',
    alignSelf: 'center',
    gap: theme.spacing(0.75),
    color: '#000',
  },
  filterByLabel: {
    marginBottom: '8px',
    fontWeight: 600,
    fontSize: '16px',
    color: '#000',
  },
  dropdownLabel: {
    fontSize: '16px', // Same as filterByLabel
    fontWeight: 'bold',
    marginBottom: '4px',
  },
  searchContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  searchInput: {
    width: '400px',
    '& .MuiInput-underline:before': {
      borderBottom: '1px solid #e0e0e0',
    },
    '& .MuiInput-underline:hover:before': {
      borderBottom: '1px solid #000',
    },
    '& .MuiInput-underline:after': {
      borderBottom: '2px solid #000',
    },
  },
  searchButton: {
    backgroundColor: '#ec6535',
    color: '#fff',
    textTransform: 'none',
    borderRadius: '20px',
    padding: '8px 24px',
    minWidth: '120px',
    minHeight: '40px',
    height: '40px',
    boxShadow: 'none',
    fontWeight: 500,
    fontSize: '14px',
    '&:hover': {
      backgroundColor: '#d4551a',
    },
  },
  filterRight: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '2px',
    flexShrink: 0,
    minWidth: '200px',
  },
  refreshContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: '2px',
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'flex-start',
      paddingTop: '1rem',
    },
    [theme.breakpoints.down('xs')]: {
      paddingTop: '1rem',
    },
  },
  padR20: {
    paddingRight: 20,
    [theme.breakpoints.down('xs')]: {
      paddingRight: 0,
    },
  },
  refreshText: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  perPageContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  hgText: {
    fontWeight: 500,
    paddingRight: theme.spacing(1),
  },
  formPerPage: {
    minWidth: 60,
    fontSize: '0.875rem',
    '& .MuiOutlinedInput-root': {
      outline: 'none !important',
      '&.Mui-focused': {
        outline: 'none !important',
        '& fieldset': {
          borderWidth: '1px !important',
        },
      },
    },
  },
  outlined: {
    padding: theme.spacing(1),
  },
  tabsContainer: {
    border: '1px solid #E0E0E0',
    borderRadius: '4px',
    backgroundColor: '#fff',
    marginTop: theme.spacing(2),
    padding: 0,
    paddingTop: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    maxHeight: 'calc(100vh - 350px)',
    overflowY: 'auto',
    overflowX: 'hidden',
  },
  tabs: {
    backgroundColor: '#fff',
    color: '#000',
    marginTop: 0,
    marginBottom: 0,
    paddingLeft: 0,
    minHeight: '48px',
    '& .MuiTabs-root': {
      paddingLeft: '0 !important',
      marginLeft: '0 !important',
    },
    '& .MuiTabs-flexContainer': {
      paddingLeft: theme.spacing(2),
      marginLeft: '0 !important',
    },
    '& .MuiTabs-scroller': {
      paddingLeft: '0 !important',
    },
    '& .MuiTab-root': {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      minWidth: 'auto',
      textTransform: 'none',
      marginTop: theme.spacing(1),
      marginLeft: 0,
      marginRight: theme.spacing(1),
      '&:first-child': {
        marginLeft: 0,
      },
      position: 'relative',
      '&::before, &::after': {
        content: '""',
        position: 'absolute',
        left: '50%',
        transform: 'translateX(-50%)',
        width: 'calc(100% - 16px)',
        height: '4px',
      },
      '&::before': {
        top: 0,
        background: 'linear-gradient(82deg, #3B173F 0%, #073270 100%)',
      },
      '&::after': {
        bottom: 0,
        backgroundColor: 'transparent',
      },
      '&.Mui-selected': {
        color: '#34a0f7',
        '&::after': {
          backgroundColor: '#34a0f7',
        },
      },
    },
    '& .MuiTabs-indicator': {
      display: 'none',
    },
  },
  paginationContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: theme.spacing(3),
  },
  tab: {
    textTransform: 'none',
    fontWeight: 500,
    minWidth: 'auto',
    padding: '12px 16px',
    '&.Mui-selected': {
      color: '#34a0f7',
    },
  },
  tabPanel: {
    padding: theme.spacing(2, 0),
  },
  tabPanelsContainer: {
    padding: theme.spacing(2),
    paddingTop: 0,
  },
  table: {
    minWidth: 650,
  },
  headerCell: {
    backgroundColor: '#F9F9FC',
    fontWeight: 700,
    padding: '12px 16px',
    borderBottom: '2px solid #E0E0E0',
  },
  tableCell: {
    padding: '12px 16px',
    borderBottom: '1px solid #E0E0E0',
  },
  tableRow: {
    '&:hover': {
      backgroundColor: '#f5f5f5',
    },
  },
}));


